import React, { useState, useEffect } from "react";
import { useSupabase } from "../SupabaseContext";
import EmailCopyButton from "./EmailCopyButton";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "./ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Progress } from "./ui/progress";
import { Alert, AlertDescription } from "./ui/alert";
import { Badge } from "./ui/badge";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Building2,
  FileText,
  Users,
} from "lucide-react";
import { useToast } from "./ui/use-toast";

const STEPS = {
  SELECT: 1,
  USERS: 2,
  CONFIRM: 3,
  SUCCESS: 4,
};

const StepHeader = ({ icon: Icon, title, description }) => (
  <div className="flex items-center space-x-3 mb-6">
    <div className="bg-gray-100 p-2 rounded-lg">
      <Icon className="w-5 h-5 text-gray-600" />
    </div>
    <div>
      <h3 className="font-medium text-lg">{title}</h3>
      {description && <p className="text-sm text-gray-500">{description}</p>}
    </div>
  </div>
);

const AccessManagement = () => {
  const supabase = useSupabase();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(STEPS.SELECT);
  const [situations, setSituations] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedSituation, setSelectedSituation] = useState("");
  const [selectedFacility, setSelectedFacility] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [user] = useState(JSON.parse(localStorage.getItem("user")));

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedFacility && selectedSituation) {
      fetchUsers();
    }
  }, [selectedFacility, selectedSituation]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchSituations(), fetchFacilities()]);
    } catch (error) {
      setError("Nepodarilo sa načítať údaje. Skúste to znova.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSituations = async () => {
    const { data, error } = await supabase
      .from("situations")
      .select("id, title")
      .eq("creator_id", user.id);

    if (error) throw error;
    setSituations(data);
  };

  const fetchFacilities = async () => {
    let query = supabase
      .from("facilities")
      .select("id, name")
      .eq("is_active", true);

    if (user.is_manager) {
      const { data: managedFacilities } = await supabase
        .from("facility_managers")
        .select("facility_id")
        .eq("user_id", user.id)
        .eq("is_active", true);

      query = query.in(
        "id",
        managedFacilities.map((f) => f.facility_id)
      );
    }

    const { data, error } = await query;
    if (error) throw error;
    setFacilities(data);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Get user IDs for users in this facility using the junction table
      const { data: userFacilities, error: ufError } = await supabase
        .from("user_facilities")
        .select("user_id")
        .eq("facility_id", selectedFacility)
        .eq("is_active", true);

      if (ufError) throw ufError;

      if (!userFacilities?.length) {
        setUsers([]);
        return;
      }

      // Get full user details for those IDs
      const { data: usersData, error: usersError } = await supabase
        .from("users")
        .select("id, first_name, last_name, email")
        .in(
          "id",
          userFacilities.map((uf) => uf.user_id)
        )
        .eq("is_active", true);

      if (usersError) throw usersError;

      // Get existing access control entries
      const { data: accessData, error: accessError } = await supabase
        .from("access_control")
        .select("user_id")
        .match({
          situation_id: selectedSituation,
          facility_id: selectedFacility,
        });

      if (accessError) throw accessError;

      const existingAccess = new Set(accessData.map((a) => a.user_id));
      setUsers(usersData);
      setSelectedUsers(Array.from(existingAccess));
    } catch (error) {
      setError("Nepodarilo sa načítať používateľov. Skúste to znova.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      // First, delete existing access records for this situation/facility
      await supabase.from("access_control").delete().match({
        situation_id: selectedSituation,
        facility_id: selectedFacility,
      });

      if (selectedUsers.length > 0) {
        // Create new access entries - one per user
        const accessData = selectedUsers.map((userId) => ({
          situation_id: selectedSituation,
          facility_id: selectedFacility,
          user_id: userId,
        }));

        // Use upsert instead of insert to handle potential duplicates
        const { error } = await supabase
          .from("access_control")
          .upsert(accessData, {
            onConflict: "situation_id,user_id",
            ignoreDuplicates: true,
          });

        if (error) {
          console.error("Submission error:", error);
          throw new Error(`Chyba pri ukladaní prístupov: ${error.message}`);
        }
      }

      toast({
        title: "Prístupy aktualizované",
        description: "Prístupy pre používateľov boli úspešne nastavené.",
        variant: "default",
      });

      setCurrentStep(STEPS.SUCCESS);
    } catch (error) {
      console.error("Error handling submit:", error);
      setError(
        error.message || "Nepodarilo sa aktualizovať prístupy. Skúste to znova."
      );

      toast({
        title: "Chyba",
        description:
          error.message ||
          "Nepodarilo sa aktualizovať prístupy. Skúste to znova.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => setCurrentStep((prev) => prev + 1);
  const handleBack = () => setCurrentStep((prev) => prev - 1);
  const handleReset = () => {
    setCurrentStep(STEPS.SELECT);
    setSelectedSituation("");
    setSelectedFacility("");
    setSelectedUsers([]);
  };

  const SelectStep = () => (
    <>
      <StepHeader
        icon={FileText}
        title="Výber situácie a skupiny"
        description="Vyberte situáciu a skupinu pre správu prístupov"
      />
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Situácia</Label>
          <Select
            value={selectedSituation}
            onValueChange={setSelectedSituation}
          >
            <SelectTrigger>
              <SelectValue placeholder="Vyberte situáciu" />
            </SelectTrigger>
            <SelectContent>
              {situations.map((situation) => (
                <SelectItem key={situation.id} value={situation.id}>
                  {situation.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Skupina</Label>
          <Select value={selectedFacility} onValueChange={setSelectedFacility}>
            <SelectTrigger>
              <SelectValue placeholder="Vyberte skupinu" />
            </SelectTrigger>
            <SelectContent>
              {facilities.map((facility) => (
                <SelectItem key={facility.id} value={facility.id}>
                  {facility.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  );

  const UsersStep = () => (
    <>
      <StepHeader
        icon={Users}
        title="Výber používateľov"
        description="Vyberte používateľov, ktorí majú mať prístup"
      />
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="selectAll"
            checked={selectedUsers.length === users.length}
            onCheckedChange={(checked) =>
              setSelectedUsers(checked ? users.map((u) => u.id) : [])
            }
          />
          <Label htmlFor="selectAll">Vybrať všetkých</Label>
        </div>
        <div className="space-y-2">
          {users.map((user) => (
            <div key={user.id} className="flex items-center space-x-2">
              <Checkbox
                id={user.id}
                checked={selectedUsers.includes(user.id)}
                onCheckedChange={(checked) =>
                  setSelectedUsers((prev) =>
                    checked
                      ? [...prev, user.id]
                      : prev.filter((id) => id !== user.id)
                  )
                }
              />
              <Label htmlFor={user.id}>
                {user.first_name} {user.last_name}
              </Label>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  const ConfirmStep = () => (
    <>
      <StepHeader
        icon={Building2}
        title="Potvrdenie prístupov"
        description="Skontrolujte a potvrďte vaše výbery"
      />
      <div className="space-y-4">
        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
          <p>
            <strong>Situácia: </strong>
            {situations.find((s) => s.id === selectedSituation)?.title}
          </p>
          <p>
            <strong>Skupina: </strong>
            {facilities.find((f) => f.id === selectedFacility)?.name}
          </p>
          <p>
            <strong>Vybraní používatelia: </strong>
            {selectedUsers.length}
          </p>
        </div>
      </div>
    </>
  );

  const SuccessStep = () => {
    // Get selected users with their emails
    const selectedUsersWithEmails = users
      .filter((user) => selectedUsers.includes(user.id))
      .map((user) => ({
        id: user.id,
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
      }));

    const emailsToShare = selectedUsersWithEmails.map((user) => user.email);

    return (
      <div className="text-center space-y-6">
        <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
          <Check className="w-6 h-6 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold">
          Prístupy boli úspešne nastavené
        </h3>
        <p className="text-gray-600">
          Zmeny boli uložené a prístupy aktualizované.
        </p>

        <Card className="mt-6">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">
                    Počet užívateľov s prístupom
                  </p>
                  <p className="text-2xl font-bold">
                    {selectedUsersWithEmails.length}
                  </p>
                </div>
                <EmailCopyButton emails={emailsToShare} />
              </div>

              <div className="space-y-2">
                <p className="font-medium text-left">Zoznam užívateľov:</p>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {selectedUsersWithEmails.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-2 bg-white rounded border text-left"
                    >
                      <span>{user.name}</span>
                      <span className="text-sm text-gray-500">
                        {user.email}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Button onClick={handleReset} className="mt-6">
          Nastaviť ďalšie prístupy
        </Button>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case STEPS.SELECT:
        return <SelectStep />;
      case STEPS.USERS:
        return <UsersStep />;
      case STEPS.CONFIRM:
        return <ConfirmStep />;
      case STEPS.SUCCESS:
        return <SuccessStep />;
      default:
        return null;
    }
  };

  const stepButtons = {
    [STEPS.SELECT]: {
      next: {
        disabled: !selectedSituation || !selectedFacility,
        text: "Ďalej",
      },
    },
    [STEPS.USERS]: {
      back: true,
      next: {
        disabled: selectedUsers.length === 0,
        text: "Kontrola",
      },
    },
    [STEPS.CONFIRM]: {
      back: true,
      next: {
        text: "Potvrdiť",
        onClick: handleSubmit,
      },
    },
  };

  if (loading && currentStep === STEPS.SELECT) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Správa prístupov</CardTitle>
        <CardDescription>
          Spravujte prístupy k situáciám pre používateľov skupiny
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="mb-6">
          <Progress value={(currentStep / 4) * 100} className="w-full" />
        </div>

        {renderStepContent()}

        {currentStep !== STEPS.SUCCESS && (
          <div className="mt-6 flex justify-between">
            {stepButtons[currentStep]?.back && (
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Späť
              </Button>
            )}
            {stepButtons[currentStep]?.next && (
              <Button
                className="ml-auto"
                disabled={
                  loading || stepButtons[currentStep].next.disabled || false
                }
                onClick={
                  stepButtons[currentStep].next.onClick
                    ? stepButtons[currentStep].next.onClick
                    : handleNext
                }
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {stepButtons[currentStep].next.text}
                {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AccessManagement;
